import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, cn, Skeleton } from '@/design-system';
import { canRemovePhoto, canRetryPhoto, isHeic, photoMutations } from '@/entities/photo';
import { useTenantDb, type PhotoRecord } from '@/shared/offline';

import { surveyCopy } from '../copy';
import { usePhotoDrop } from '../hooks/usePhotoDrop';

export interface PhotoStripProps {
    photos: readonly PhotoRecord[];
    /** Maakt de strip zelf een dropzone voor foto's. */
    onAddPhotos?: (files: File[]) => void;
    /** Zet "Foto verwijderen" in de viewer van een foto die nog weg mag. */
    onRemovePhoto?: (clientUuid: string) => void;
}

const ignore = () => {};

/**
 * De foto's onder een vraag: een raster over de volle breedte van de kaart, met
 * voorbeelden die groot genoeg zijn om op locatie te zien wat je vastlegde.
 * Tikken opent de foto groot; verwijderen is daar een tweede, eigen knop van
 * 44 px, zodat er geen foto weggaat met één misser.
 *
 * De foto's zitten als Blob in Dexie, dus ze zijn ook zonder netwerk zichtbaar.
 * Een foto die alleen op de server staat toont zijn `thumb` (groot: `web`). Kan
 * de browser het formaat niet tonen (HEIC buiten Safari), dan valt het raster
 * terug op de serverthumbnail of op de arceerplaceholder uit het ontwerp - de
 * server zet het bestand om.
 *
 * Onder het raster staat wat er nog moet gebeuren: hoeveel foto's op verzending
 * wachten, en welke de server niet aannam. Die laatste zijn niet kwijt - de
 * bytes staan er nog - dus je kunt ze hier opnieuw aanbieden in plaats van er
 * pas bij het rapport achter te komen.
 */
export function PhotoStrip({ photos, onAddPhotos, onRemovePhoto }: PhotoStripProps) {
    const drop = usePhotoDrop(onAddPhotos ?? ignore);
    const [open, setOpen] = useState<string | null>(null);
    const openPhoto = photos.find((photo) => photo.clientUuid === open);
    const retry = usePhotoRetry();

    if (photos.length === 0) {
        return null;
    }

    const failed = photos.filter(canRetryPhoto);
    const waiting = photos.filter(
        (photo) => photo.state === 'pending' || photo.state === 'uploading',
    ).length;

    return (
        <div
            data-testid="photo-strip"
            data-swipe="off"
            data-dropzone={drop.active ? 'active' : undefined}
            className={cn(
                'border-t border-[var(--border-subtle)] px-[10px] py-[6px]',
                drop.active &&
                    'bg-[var(--accent-tint)] outline-2 outline-offset-[-4px] outline-[var(--accent)] outline-dashed',
            )}
            {...(onAddPhotos ? drop.handlers : {})}
        >
            <ul className="m-0 grid list-none gap-[6px] p-0 [grid-template-columns:repeat(auto-fill,minmax(104px,1fr))]">
                {photos.map((photo) => (
                    <li key={photo.clientUuid}>
                        <button
                            type="button"
                            aria-label={surveyCopy.question.viewPhoto(photo.caption ?? '')}
                            onClick={() => setOpen(photo.clientUuid)}
                            className="block w-full cursor-pointer rounded-[7px] border-0 bg-transparent p-0 outline-offset-2 focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                        >
                            <PhotoThumb photo={photo} />
                        </button>
                    </li>
                ))}
            </ul>

            {drop.active ? (
                <p className="m-0 mt-2 text-[12px] text-[var(--accent-dark)]">
                    {surveyCopy.question.dropHere}
                </p>
            ) : (
                <PhotoQueue
                    failed={failed}
                    waiting={waiting}
                    {...(onAddPhotos ? { onRetry: retry } : {})}
                />
            )}

            {openPhoto ? (
                <PhotoViewer
                    photo={openPhoto}
                    onClose={() => setOpen(null)}
                    {...(onAddPhotos && canRetryPhoto(openPhoto)
                        ? { onRetry: () => void retry([openPhoto.clientUuid]) }
                        : {})}
                    {...(onRemovePhoto && canRemovePhoto(openPhoto)
                        ? {
                              onRemove: () => {
                                  onRemovePhoto(openPhoto.clientUuid);
                                  setOpen(null);
                              },
                          }
                        : {})}
                />
            ) : null}
        </div>
    );
}

/**
 * Wat er met deze foto's nog moet gebeuren. Amber, nooit rood: ze staan veilig
 * op het apparaat, ook als de server ze (nog) niet aannam.
 */
function PhotoQueue({
    failed,
    waiting,
    onRetry,
}: {
    failed: readonly PhotoRecord[];
    waiting: number;
    onRetry?: (clientUuids: readonly string[]) => void;
}) {
    if (failed.length === 0 && waiting === 0) {
        return null;
    }

    return (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p
                className={cn(
                    'm-0 text-[12px] leading-[1.4]',
                    failed.length > 0
                        ? 'text-[var(--warn-text)]'
                        : 'text-[var(--text-secondary)]',
                )}
            >
                {failed.length > 0
                    ? surveyCopy.question.photoRejected(failed.length)
                    : surveyCopy.question.photoWaiting(waiting)}
            </p>
            {failed.length > 0 && onRetry ? (
                <Button
                    variant="outline"
                    size="md"
                    onClick={() => onRetry(failed.map((photo) => photo.clientUuid))}
                >
                    {surveyCopy.question.photoRetryAll(failed.length)}
                </Button>
            ) : null}
        </div>
    );
}

/**
 * Eén foto groot, over het scherm heen. Sluiten met de knop, met Escape of door
 * naast de foto te tikken; verwijderen alleen met de eigen knop.
 */
function PhotoViewer({
    photo,
    onClose,
    onRemove,
    onRetry,
}: {
    photo: PhotoRecord;
    onClose: () => void;
    onRemove?: () => void;
    onRetry?: () => void;
}) {
    const local = useObjectUrl(photo.blob);
    const url = local ?? photo.urls?.web ?? photo.urls?.thumb ?? null;
    const caption = photo.caption ?? '';
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        closeRef.current?.focus();

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={surveyCopy.question.photoAlt(caption)}
            className="fixed inset-0 z-50 flex flex-col bg-[var(--ink-800)] p-3"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="flex min-h-0 flex-1 items-center justify-center" onClick={onClose}>
                {url ? (
                    <img
                        src={url}
                        alt={surveyCopy.question.photoAlt(caption)}
                        className="max-h-full max-w-full rounded-[7px] object-contain"
                        onClick={(event) => event.stopPropagation()}
                    />
                ) : (
                    <Skeleton variant="hatch" width={280} height={210} caption={caption} />
                )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
                <span className="min-w-0 flex-1 text-[13px] text-[var(--text-on-dark-2)]">
                    {photo.state === 'failed'
                        ? (photo.lastError ?? surveyCopy.question.photoFailed)
                        : caption}
                </span>
                <div className="flex flex-wrap gap-2">
                    {onRetry ? (
                        <Button variant="onDark" size="md" onClick={onRetry}>
                            {surveyCopy.question.photoRetry}
                        </Button>
                    ) : null}
                    {onRemove ? (
                        <Button variant="warn" size="md" onClick={onRemove}>
                            {surveyCopy.question.removePhoto}
                        </Button>
                    ) : null}
                    <Button ref={closeRef} variant="onDark" size="md" onClick={onClose}>
                        {surveyCopy.question.closePhoto}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function PhotoThumb({ photo }: { photo: PhotoRecord }) {
    const local = useObjectUrl(photo.blob);
    const [broken, setBroken] = useState<readonly string[]>([]);
    const url =
        [local, photo.urls?.thumb ?? null].find(
            (candidate): candidate is string => candidate !== null && !broken.includes(candidate),
        ) ?? null;
    const caption = photo.caption ?? '';
    const status =
        photo.state === 'uploaded'
            ? null
            : photo.state === 'failed'
              ? surveyCopy.question.photoFailed
              : surveyCopy.question.photoPending;

    if (!url) {
        return (
            <div className="relative aspect-[4/3] w-full" data-photo-state={photo.state}>
                <Skeleton
                    variant="hatch"
                    width="100%"
                    height="100%"
                    bordered
                    caption={
                        isHeic(photo.mimeType)
                            ? surveyCopy.question.photoConverting(caption)
                            : caption
                    }
                />
                {status ? <StatusDot label={status} /> : null}
            </div>
        );
    }

    return (
        <figure
            data-photo-state={photo.state}
            className="relative m-0 aspect-[4/3] w-full overflow-hidden rounded-[7px] border border-[var(--placeholder-border)]"
        >
            <img
                src={url}
                alt={surveyCopy.question.photoAlt(caption)}
                className="h-full w-full object-cover"
                onError={() => setBroken((current) => [...current, url])}
            />
            <figcaption className="absolute bottom-[5px] left-[5px] rounded-[3px] bg-[var(--placeholder-caption-bg)] px-1 py-[2px] font-[family-name:var(--font-mono)] text-[9px] text-[var(--text-secondary)]">
                {caption}
            </figcaption>
            {status ? <StatusDot label={status} /> : null}
        </figure>
    );
}

/** Nog niet op de server: een amber stip. Nooit rood - de foto staat veilig op het apparaat. */
function StatusDot({ label }: { label: string }) {
    return (
        <span
            role="img"
            aria-label={label}
            title={label}
            className="absolute top-[5px] right-[5px] h-[8px] w-[8px] rounded-full border border-[var(--surface-card)] bg-[var(--warn-field)]"
        />
    );
}

/**
 * Opnieuw aanbieden van foto's die de server weigerde. Lukt het schrijven niet
 * (de opname is net ingediend), dan blijft de foto staan zoals hij stond en
 * blijft de melding erboven zijn werk doen.
 */
function usePhotoRetry(): (clientUuids: readonly string[]) => Promise<void> {
    const db = useTenantDb();

    return useCallback(
        async (clientUuids: readonly string[]) => {
            if (!db) {
                return;
            }

            for (const clientUuid of clientUuids) {
                try {
                    await photoMutations.retry(clientUuid, db);
                } catch {
                    return;
                }
            }
        },
        [db],
    );
}

/**
 * Eén object-URL per foto. De thumbnail is gekoppeld aan `photo.clientUuid`,
 * dus de blob wisselt nooit binnen dezelfde component; lazy init plus een
 * opruiming bij unmount is daarmee genoeg - en scheelt een extra render per
 * foto.
 *
 * Lukt het niet, dan valt de strip terug op de arceerplaceholder. Een foto die
 * niet te tonen is mag nooit het opnamescherm meeslepen: hij staat al lokaal
 * opgeslagen en gaat gewoon mee in de sync.
 */
function useObjectUrl(blob: Blob | null): string | null {
    const [url] = useState<string | null>(() => {
        if (!blob || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
            return null;
        }

        try {
            return URL.createObjectURL(blob);
        } catch {
            return null;
        }
    });

    useEffect(() => {
        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [url]);

    return url;
}
